import { Dispatch } from 'redux'
import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { loadDeliveries } from './actions'
import { Delivery, PacketType } from './types'

const baseUrl = `${config.backendUrl}/api/trade-document-delivery`

export const prepareDelivery = (
  masterTradeId: string,
  subTradeIds: string[],
  packetType: PacketType = 'settled',
) =>
  client
    .post(`${baseUrl}/prepare`, { masterTradeId, subTradeIds, packetType })
    .then(res => ({ success: true, payload: res.data }))
    .catch(e => ({ success: false, payload: e.response?.data?.message ?? 'Failed to prepare delivery' }))

export const markSent = (
  masterTradeId: string,
  subTradeIds: string[],
  packetType: PacketType = 'settled',
) =>
  client
    .post(`${baseUrl}/mark-sent`, { masterTradeId, subTradeIds, packetType })
    .then(res => ({ success: true, payload: res.data }))
    .catch(e => ({ success: false, payload: e.response?.data?.message ?? 'Failed to mark sent' }))

// LOS-6471 failure recording: the prepare/send operations report their own
// failures so the Delivery icon can render Prep Failed / Send Failed.
export const markPrepFailed = (
  masterTradeId: string,
  subTradeIds: string[],
  packetType: PacketType = 'settled',
) =>
  client
    .post(`${baseUrl}/mark-prep-failed`, { masterTradeId, subTradeIds, packetType })
    .then(res => ({ success: true, payload: res.data }))
    .catch(e => ({ success: false, payload: e.response?.data?.message ?? 'Failed to record prep failure' }))

export const markSendFailed = (
  masterTradeId: string,
  subTradeIds: string[],
  packetType: PacketType = 'settled',
) =>
  client
    .post(`${baseUrl}/mark-send-failed`, { masterTradeId, subTradeIds, packetType })
    .then(res => ({ success: true, payload: res.data }))
    .catch(e => ({ success: false, payload: e.response?.data?.message ?? 'Failed to record send failure' }))

/**
 * Deliveries for a batch of sub-trades WITHOUT touching the store.
 *
 * The trade-list Stale Documents filter needs delivery records for every visible
 * trade — far wider than the per-opened-trade scope the detail views load.
 * Dispatching a page-wide batch into the shared slice would leave the Documents
 * tab holding records for trades the user never opened, so this hands the payload
 * back for the caller to keep in local state.
 *
 * Staleness (`staleSince`, ADR_LOS-027) is derived server-side by this endpoint.
 *
 * `data` is null ONLY on failure, and never null on success — so a failed request
 * cannot be mistaken for "fetched, found nothing". That distinction is load-bearing
 * for the caller: an empty array means no trade has a stale packet, while null means
 * there is no answer yet and the filter must not narrow the list. Returning [] on
 * failure made a dropped request look like a definitive "nothing is stale".
 *
 * An empty `subTradeIds` is a genuine empty success — there was nothing to ask about.
 */
export const fetchDeliveriesBySubTrades = async (
  subTradeIds: string[],
): Promise<{ success: boolean; data: Delivery[] | null }> => {
  if (!subTradeIds.length) return { success: true, data: [] }
  try {
    const res = await client.post(`${baseUrl}/list`, { subTradeIds })
    return { success: true, data: (res.data as unknown as Delivery[]) ?? [] }
  } catch {
    return { success: false, data: null }
  }
}

export const loadDeliveriesBySubTrades =
  (subTradeIds: string[]) => async (dispatch: Dispatch) => {
    if (!subTradeIds.length) {
      dispatch(loadDeliveries([]))
      return
    }
    try {
      // The read source is decided server-side by the read-source router
      // (see portal-backend src/read-source/README.md), not by the client.
      const res = await client.post(`${baseUrl}/list`, { subTradeIds })
      dispatch(loadDeliveries((res.data as unknown as Delivery[]) ?? []))
    } catch {
      dispatch(loadDeliveries([]))
    }
  }
